import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, Building2, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import JunagoLogo from "../components/JunagoLogo";

export default function RegisterVendor() {
  const { user, register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role === "vendor") return <Navigate to="/vendor/pilotage" replace />;
  if (user) return <Navigate to="/mes-reservations" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, role: "vendor" });
      navigate("/vendor/pilotage");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-violet-900/80" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <Link to="/">
            <JunagoLogo size={34} tone="light" />
          </Link>
          <div>
            <div className="text-xs tracking-[0.22em] uppercase font-bold text-violet-300">Espace professionnel</div>
            <h1 className="font-heading text-4xl font-medium tracking-tight mt-3 max-w-sm leading-tight">
              Pilotez votre flotte depuis un seul outil.
            </h1>
            <ul className="text-slate-200 mt-6 space-y-2 text-sm">
              <li className="flex items-center gap-2">-- Contrats & états des lieux automatisés</li>
              <li className="flex items-center gap-2">-- Encaissements sécurisés via Stripe</li>
              <li className="flex items-center gap-2">-- Calendrier de flotte & tâches automatiques</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 py-12">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="lg:hidden mb-8 inline-block">
            <JunagoLogo size={34} tone="dark" />
          </Link>

          <div className="overline mb-2">Espace loueur</div>
          <h2 className="font-heading text-3xl font-medium tracking-tight text-slate-900">Devenez loueur.</h2>
          <p className="text-sm text-slate-500 mt-2">
            Déjà un compte ?{" "}
            <Link to="/login" className="text-violet-600 font-medium hover:underline">Connectez-vous</Link>
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="vendor-register-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nom complet" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Marc Dubois" testid="vreg-name" required />
              <Field label="Société" value={form.company} onChange={(v) => setForm({ ...form, company: v })} placeholder="Paris Luxury Cars" testid="vreg-company" />
            </div>
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="vous@exemple.com" testid="vreg-email" required />
            <Field label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+33 6 12 34 56 78" testid="vreg-phone" />
            <Field label="Mot de passe" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} placeholder="6 caractères minimum" testid="vreg-password" required />

            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="vreg-error">{error}</div>}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full btn-primary font-semibold text-sm disabled:opacity-50"
              data-testid="vreg-submit"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Créer mon compte loueur
            </button>
            <p className="text-xs text-slate-500 text-center">
              En continuant, vous acceptez les conditions d'utilisation de Junago.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600 focus:border-violet-600"
        data-testid={testid}
      />
    </div>
  );
}
